import { useState, useEffect, useCallback } from 'react';
import type { MaterialWithFileMetadata } from '../types';
import {
  getMaterials,
  createMaterial,
  getMaterialById,
  updateMaterial,
  deleteMaterial,
  toggleMaterialEnabled,
  toggleMaterialAutoReferenced,
  searchMaterials,
  getProjectMaterials,
  createProjectMaterial,
  getProjectMaterialById,
  updateProjectMaterial,
  deleteProjectMaterial,
  toggleProjectMaterialEnabled,
  toggleProjectMaterialAutoReferenced,
  searchProjectMaterials,
  associateMaterialsWithProject,
} from '../services/materials';
import Loading from '../components/common/Loading';

interface UseMaterialsOptions {
  projectId?: string;
  initialFetch?: boolean;
  hasOutline?: boolean;
}

interface CreateTextMaterialData {
  filename: string;
  content: string;
}

export function useMaterials({ projectId, initialFetch = true, hasOutline = false }: UseMaterialsOptions = {}) {
  const [materials, setMaterials] = useState<MaterialWithFileMetadata[]>([]);
  const [currentMaterial, setCurrentMaterial] = useState<MaterialWithFileMetadata | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingText, setIsCreatingText] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchMaterials = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const fetchedMaterials = projectId ? await getProjectMaterials(projectId, undefined, hasOutline) : await getMaterials(undefined, hasOutline);
      setMaterials(fetchedMaterials);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch materials'));
    } finally {
      setIsLoading(false);
    }
  }, [projectId, hasOutline]);

  useEffect(() => {
    if (initialFetch) {
      fetchMaterials();
    }
  }, [initialFetch, fetchMaterials]);

  const handleCreateMaterial = async (formData: FormData) => {
    setIsUploadingFile(true);
    setError(null);
    try {
      const newMaterial = projectId
        ? await createProjectMaterial(projectId, formData)
        : await createMaterial(formData);
      await fetchMaterials(); // Fetch fresh list to ensure we have all metadata
      return newMaterial;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to create material'));
      throw err;
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleCreateTextMaterial = async (data: CreateTextMaterialData) => {
    setIsCreatingText(true);
    setError(null);
    try {
      const formData = new FormData();
      const textBlob = new Blob([data.content], { type: 'text/plain' });
      formData.append('file', textBlob, data.filename);
      formData.append('type', 'text');
      
      const newMaterial = projectId
        ? await createProjectMaterial(projectId, formData)
        : await createMaterial(formData);
      await fetchMaterials(); // Fetch fresh list to ensure we have all metadata
      return newMaterial;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to create text material'));
      throw err;
    } finally {
      setIsCreatingText(false);
    }
  };

  const handleGetMaterialById = async (materialId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const material = projectId
        ? await getProjectMaterialById(projectId, materialId)
        : await getMaterialById(materialId);
      setCurrentMaterial(material);
      return material;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch material'));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateMaterial = async (
    materialId: string,
    data: { tagIds?: string[]; projectId?: string | null }
  ) => {
    setIsLoading(true);
    setError(null);
    try {
      const updatedMaterial = projectId
        ? await updateProjectMaterial(projectId, materialId, data)
        : await updateMaterial(materialId, data);
      setMaterials(prev =>
        prev.map(material =>
          material._id === materialId ? {...material, ...updatedMaterial} : material
        )
      );
      if (currentMaterial?._id === materialId) {
        setCurrentMaterial({...currentMaterial, ...updatedMaterial});
      }
      return updatedMaterial;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to update material'));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteMaterial = async (materialId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      projectId
        ? await deleteProjectMaterial(projectId, materialId)
        : await deleteMaterial(materialId);
      await fetchMaterials(); // Fetch fresh list
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to delete material'));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleMaterialEnabled = async (materialId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const updatedMaterial = projectId
        ? await toggleProjectMaterialEnabled(projectId, materialId)
        : await toggleMaterialEnabled(materialId);
      setMaterials(prev =>
        prev.map(material =>
          material._id === materialId ? {...material, ...updatedMaterial} : material
        )
      );
      return updatedMaterial;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to toggle material status'));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleMaterialAutoReferenced = async (materialId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const updatedMaterial = projectId
        ? await toggleProjectMaterialAutoReferenced(projectId, materialId)
        : await toggleMaterialAutoReferenced(materialId);
      setMaterials(prev =>
        prev.map(material =>
          material._id === materialId ? {...material, ...updatedMaterial} : material
        )
      );
      return updatedMaterial.autoReferenced;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to toggle material auto referenced'));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      await fetchMaterials();
      return;
    }

    setIsSearching(true);
    setError(null);
    try {
      const results = projectId
        ? await searchProjectMaterials(projectId, searchQuery)
        : await searchMaterials(searchQuery);
      const validMaterials = results
        .map(result => result.material)
        .filter((material): material is MaterialWithFileMetadata => material !== undefined);
      setMaterials(validMaterials);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to search materials'));
    } finally {
      setIsSearching(false);
    }
  };

  const handleAssociateMaterialsWithProject = async (materialIds: string[]) => {
    if (!projectId) {
      throw new Error('Project ID is required to associate materials');
    }

    setIsLoading(true);
    setError(null);
    try {
      const associatedMaterials = await associateMaterialsWithProject(projectId, materialIds);
      await fetchMaterials(); // Refresh the list after association
      return associatedMaterials;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to associate materials with project'));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    // State
    materials,
    currentMaterial,
    searchQuery,
    isSearching,
    isLoading,
    isCreatingText,
    isUploadingFile,
    error,

    // Actions
    setSearchQuery,
    fetchMaterials,
    createMaterial: handleCreateMaterial,
    createTextMaterial: handleCreateTextMaterial,
    getMaterialById: handleGetMaterialById,
    updateMaterial: handleUpdateMaterial,
    deleteMaterial: handleDeleteMaterial,
    toggleMaterialEnabled: handleToggleMaterialEnabled,
    toggleMaterialAutoReferenced: handleToggleMaterialAutoReferenced,
    handleSearch,
    associateMaterialsWithProject: handleAssociateMaterialsWithProject,
  };
} 